import { Box, Button, Divider, FormLabel, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getCoordinatesByAddress } from '../../api/nominatium';
import { FormInputPhone } from '../../form-components/FormInputPhone';
import { FormInputRadio } from '../../form-components/FormInputRadio';
import { FormInputText } from '../../form-components/FormInputText';
import { FormInputDropdown } from '../../form-components/FormInputDropdown';
import { FormInputBRN } from '../../form-components/FormInputBRN';
import { koreanBanks } from '../../constants/constants';
import { errorMessages } from '../../utils/methods';
import { updateManager } from '../../api/member-management';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';

const options = [
  {
    label: 'Individual',
    value: 'INDIVIDUAL',
  },
  {
    label: 'Entity',
    value: 'CORPORATION',
  },
];

export default function StoreTab1({ setSelectedTab, createStore, centerDetailInfo, branchInfo, centerId }) {
  const user = useSelector(state => state.user.user);

  const newUpdateData = {
    businessName: branchInfo?.businessName,
    businessNumber: branchInfo?.businessNumber,
    businessClassification: branchInfo?.businessClassification,
    businessType: branchInfo?.businessType,
    bankName: branchInfo?.bankName || '',
    accountNumber: branchInfo?.accountNumber || '',
    accountHolder: branchInfo?.accountHolder || '',
    representativeNumber: branchInfo?.representativeNumber,
    email: branchInfo?.email,
    address: branchInfo?.address,
    addressDetail: branchInfo?.addressDetail,
    homepage: branchInfo?.homepage,
    name: centerDetailInfo?.name,
    username: centerDetailInfo?.username,
    phoneNumber: centerDetailInfo?.phoneNumber,
    email_1: centerDetailInfo?.email,
  };

  const { t } = useTranslation();
  const toast = useToast();

  const { handleSubmit, control, setValue, setError, reset } = useForm({
    defaultValues: newUpdateData,
  });

  const scriptUrl = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
  const open = useDaumPostcodePopup(scriptUrl);

  const submit = async values => {
    if (values.password !== values.verifyPassword) {
      return setError('verifyPassword', {
        type: 'custom',
        message: 'very error',
      });
    }

    try {
      const update = {
        managerInfo: {
          username: values.username,
          name: values.name,
          ...(values.password ? { password: values.password } : {}),
          phoneNumber: values.phoneNumber,
          email: values.email_1,
        },
        centerInfo: {
          businessName: values.businessName,
          businessNumber: values.businessNumber,
          businessClassification: values.businessClassification,
          businessType: values.businessType,
          bankName: values.bankName,
          accountNumber: values.accountNumber,
          accountHolder: values.accountHolder,
          postalCode: values.postalCode,
          city: values.city,
          address: values.address,
          addressDetail: values.addressDetail,
          latitude: values.latitude,
          longitude: values.longitude,
          representativeNumber: values.representativeNumber,
          email: values.email,
          homepage: values.homepage,
        },
      };
      const response = await updateManager(user.token, centerDetailInfo.id, update);
      toast.success(t('Successfully updated'));
    } catch (error) {
      toast.error(errorMessages(error));
      reset(values);
    }
  };

  const handleComplete = async data => {
    setValue('city', data.sigungu);
    // 사용자가 선택한 주소 타입에 따라 도로명 또는 지번 주소 설정
    let fullAddress = data.addressType === 'R' ? data.roadAddress : data.jibunAddress;
    // 참고항목 추가 (법정동명, 건물명)
    let extraAddress = '';
    if (data.bname !== '') {
      extraAddress += data.bname;
    }
    if (data.buildingName !== '') {
      extraAddress += extraAddress !== '' ? ', ' + data.buildingName : data.buildingName;
    }
    if (extraAddress !== '') {
      fullAddress += ` (${extraAddress})`;
    }
    setValue('address', fullAddress);
    setValue('postalCode', data.zonecode);
    await getCoordinatesByAddress(data.addressEnglish).then(data => {
      setValue('latitude', data[0].coordinate.latitude);
      setValue('longitude', data[0].coordinate.longitude);
      // setValue('region', data.sigungu);
    });
  };

  const handleClick = () => {
    open({ onComplete: handleComplete });
  };

  useEffect(() => {
    reset(newUpdateData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerDetailInfo]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ width: '508px', textAlign: 'start' }}>
          <Typography
            className="header1"
            sx={{ fontWeight: '700', fontSize: '16px', lineHeight: '24px', color: '#3d3d3d' }}>
            {t('Business Information')}
          </Typography>
        </Box>
        <Box sx={{ width: '50%', textAlign: 'start' }}>
          <Typography
            className="header1"
            sx={{ fontWeight: '700', fontSize: '16px', lineHeight: '24px', color: '#3d3d3d' }}>
            {t('Representative Information')}
          </Typography>
        </Box>
      </Box>
      <Box style={{ display: 'flex', columnGap: '50px' }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box className="form1">
              <Typography className="header1">{t('Business Name*')}</Typography>
              <FormInputText
                control={control}
                name="businessName"
                rules={{ required: true }}
                placeholder={t('Branch Name')}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">{t('Employer Identification Number*')}</Typography>
              <FormInputBRN
                control={control}
                name="businessNumber"
                rules={{ required: true }}
                sx={{ width: '405px' }}
                placeholder={t('Enter the business identification number')}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '40px', mt: 3 }}>
              <FormLabel name="controlled-radio-buttons-group" sx={{ color: '#0f7f85', fontWeight: 500 }}>
                {t('Employer Type*')}
              </FormLabel>
              <FormInputRadio
                name="businessClassification"
                rules={{ required: true }}
                control={control}
                options={options}
                row
              />
            </Box>
            <Box className="form1">
              <Typography className="header1">은행명</Typography>
              <FormInputDropdown
                control={control}
                name="bankName"
                options={koreanBanks}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">계좌번호</Typography>
              <FormInputText
                control={control}
                name="accountNumber"
                placeholder="계좌번호를 입력해 주세요"
                sx={{ width: '405px' }}
                onChange={e => {
                  const filtered = e.target.value.replace(/[^0-9-]/g, '');
                  setValue('accountNumber', filtered);
                }}
              />
              <Typography className="header1">예금주</Typography>
              <FormInputText
                control={control}
                name="accountHolder"
                placeholder="예금주를 입력해 주세요"
                sx={{ width: '405px' }}
                onChange={e => {
                  const filtered = e.target.value.replace(/[^가-힣a-zA-Zㄱ-ㅎㅏ-ㅣ\s]/g, '');
                  setValue('accountHolder', filtered);
                }}
              />

              <Typography className="header1">{t('Type of Business')}</Typography>
              <FormInputText
                control={control}
                name="businessType"
                placeholder={t('Enter the type of business')}
                sx={{ width: '405px' }}
              />

              <Typography className="header1">{t('Representative number (optional)')}</Typography>
              <FormInputPhone
                control={control}
                sx={{ width: '405px' }}
                setValue={setValue}
                rules={{ required: true }}
                name="representativeNumber"
                placeholder={t('Enter the phone number')}
                isLandline={true}
              />

              <Typography className="header1">{t('E-mail*')}</Typography>
              <FormInputText
                control={control}
                name="email"
                type="email"
                rules={{ required: true }}
                placeholder={t('Enter the E-mail address')}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">{t('Mailing Address*')}</Typography>
              <Stack sx={{ flexDirection: 'row', mb: 2.25 }}>
                <FormInputText
                  control={control}
                  name="address"
                  sx={{ width: '334px' }}
                  size="small"
                  placeholder={t('Mailing Address*')}
                />
                <Button
                  onClick={handleClick}
                  variant="contained"
                  color="primary"
                  sx={{
                    height: 40,
                    bgcolor: '#F8FBFB',
                    boxShadow: 'none',
                    color: '#0F7F85',
                    fontSize: '13px',
                    fontWeight: 500,
                    lineHeight: '15.51px',
                    ml: 1,
                    '&:hover': { color: '#fff' },
                  }}>
                  검색
                </Button>
              </Stack>

              <FormInputText
                color="info"
                control={control}
                sx={{ width: '405px' }}
                size="small"
                fullWidth
                variant="outlined"
                name="addressDetail"
                placeholder={t('Address details')}
              />
              <Typography className="header1">{t('Web site')}</Typography>
              <FormInputText
                color="info"
                control={control}
                sx={{
                  width: '405px',
                  mb: '20px',
                  '.css-1x51dt5-MuiInputBase-input-MuiInput-input': {
                    fontSize: '16px',
                  },
                }}
                size="small"
                fullWidth
                variant="outlined"
                name="homepage"
                placeholder="홈페이지 주소를 입력해 주세요"
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box className="form1" sx={{ mt: '21px' }}>
              <Typography className="header1">{t('Name*')}</Typography>
              <FormInputText
                control={control}
                name="name"
                rules={{ required: true }}
                placeholder={t('Enter executor name')}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">{t('ID*')}</Typography>
              <FormInputText
                control={control}
                name="username"
                rules={{ required: true }}
                placeholder={t('Enter your ID')}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">{t('Password')}</Typography>
              <FormInputText
                control={control}
                name="password"
                type="password"
                autoComplete="new-password"
                placeholder={t(
                  'Passwords must contain at least two of the following: uppercase letters, lowercase letters, numbers, and symbols',
                )}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">{t('Confirm Password')}</Typography>
              <FormInputText
                control={control}
                name="verifyPassword"
                type="password"
                autoComplete="new-password"
                placeholder={t('Must match the previous entry')}
                sx={{ width: '405px' }}
              />

              <Typography className="header1">{t('Phone Number*')}</Typography>
              <FormInputPhone
                control={control}
                setValue={setValue}
                rules={{ required: true }}
                name="phoneNumber"
                placeholder={t('Enter your phone number')}
                sx={{ width: '405px' }}
              />

              <Typography className="header1">{t('E-mail*')}</Typography>
              <FormInputText
                control={control}
                name="email_1"
                type="email"
                rules={{ required: true }}
                placeholder={t('Enter your E-mail address')}
                sx={{ width: '405px' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ mt: '43px', border: '1px solid #E7E7E7' }} />
      <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3, pb: '45px' }}>
        <Button
          href="/store"
          variant="contained"
          sx={{
            borderRadius: '5px',
            minWidth: '103px',
            height: '40px',
            bgcolor: '#98A6AD',
            color: '#fff',
            '&:hover': { background: '#ddd' },
            fontSize: '13px',
            fontWeight: 700,
          }}>
          {t('Cancel')}
        </Button>
        <Button
          id="store-create-button"
          onClick={handleSubmit(submit)}
          // disabled={user.userPoint}
          variant="contained"
          color="primary"
          sx={{ borderRadius: '5px', minWidth: '183px', height: '40px', ml: 1.25 }}>
          {t('Save')}
        </Button>
      </Box>
    </Box>
  );
}
